import type { CSSProperties } from 'react';

/**
 * Avatar — fotoğraf varsa fotoğraf, yoksa baş harfler.
 *
 * Neden ayrı bir bileşen: "yuvarlak içinde baş harf" bu üründe üç ayrı yerde
 * geçiyor (panelin üst çubuğu, admin, anasayfanın yorumları) ve her seferinde
 * aynı üç soruyu soruyor — baş harfler nasıl çıkarılır, fotoğraf gelince ne
 * olur, fotoğraf 404 verirse ne görünür. Cevap tek yerde dursun diye burada.
 * Panelin kendi `.dash-avatar`'ı henüz bunu KULLANMIYOR (çalışan bir parçayı
 * bu iş için elden geçirmedik); yeni yerlerde bu tercih edilir.
 *
 * ── Fotoğraf düşerse ne olur ──────────────────────────────────────────────
 *
 * Baş harfler HER ZAMAN basılıyor, fotoğraf onların ÜSTÜNE konumlanıyor.
 * `onError` ile durum tutup geri dönmek gerekmiyor: dosya 404 verirse tarayıcı
 * `alt=""` olduğu için hiçbir şey çizmez ve altındaki baş harfler görünür
 * kalır. Yani geri düşüş JavaScript'siz ve ilk boyamada doğru — sunucuda
 * üretilen HTML ile tarayıcının gördüğü aynı, hidrasyon farkı yok.
 *
 * ── Erişilebilirlik ────────────────────────────────────────────────────────
 *
 * Kök `aria-hidden`: avatar hiçbir yerde tek başına durmuyor, yanında kişinin
 * adı zaten METİN olarak yazılı. Okutulsaydı ekran okuyucu "D A, Defne Aksoy"
 * derdi. Bu yüzden fotoğrafın `alt`'ı da boş — dekoratif.
 *
 * ── Türkçe büyük harf ──────────────────────────────────────────────────────
 *
 * `toUpperCase()` yerine `toLocaleUpperCase(locale)`: Türkçede "i" → "İ",
 * İngilizcede "I". Locale dışarıdan geliyor çünkü bileşen hangi dilde
 * gösterildiğini bilemez; çağıran taraf `useLocale()` ile verir. Yanlış locale
 * "İpek" adını "IPEK" diye kısaltır — küçük ama yanlış.
 */

/**
 * Ad → baş harfler. Tek kelimeyse tek harf, çok kelimeyse İLK ve SON kelimenin
 * baş harfleri ("Ayşe Nur Demir" → AD). Ortadaki adlar atlanıyor: üç harf
 * 44px'lik daireye sığmıyor ve soyadı kimliğin taşıyıcısı.
 *
 * `Array.from` ile bölünüyor, `word[0]` ile değil: `[0]` bir UTF-16 birimi
 * alır ve BMP dışındaki bir harfte (ya da emoji taşıyan bir adda) yarım
 * karakter üretir.
 */
export function initialsOf(name: string, locale = 'tr'): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  const head = (word: string | undefined) => (word ? (Array.from(word)[0] ?? '') : '');

  const first = head(words[0]);
  const last = words.length > 1 ? head(words[words.length - 1]) : '';

  return (first + last).toLocaleUpperCase(locale);
}

type AvatarProps = {
  /** Kişinin tam adı. Baş harfler bundan türüyor; ayrıca fotoğraf yoksa tek kaynak. */
  name: string;
  /** Fotoğrafın yolu (`/avatars/defne-aksoy.jpg`). Yoksa baş harfler kalır. */
  src?: string;
  /** Çap, piksel. Verilmezse CSS'teki varsayılan (44px) geçerli. */
  size?: number;
  /** Büyük harf üretimi için sayfa dili — çağıran `useLocale()` ile verir. */
  locale?: string;
  className?: string;
};

export function Avatar({ name, src, size, locale, className }: AvatarProps) {
  return (
    <span
      aria-hidden="true"
      className={className ? `avatar ${className}` : 'avatar'}
      style={size ? ({ '--avatar-size': `${size}px` } as CSSProperties) : undefined}
    >
      <span className="avatar-initials">{initialsOf(name, locale)}</span>
      {src ? (
        <img alt="" className="avatar-photo" decoding="async" loading="lazy" src={src} />
      ) : null}
    </span>
  );
}
